import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { isBoolean } from '@unistocks/shared/utils/util';
import { AuthTokenDto } from '@unistocks/types/auth';
import { DateTime } from 'luxon';
import { ColorSchemeName } from 'react-native';

export interface ImpersonationState {
  /**
   * The original admin token stored while impersonating a tenant user.
   */
  impersonatingUser?: AuthTokenDto;
}

export interface AppState {
  token?: AuthTokenDto;
  /**
   * Impersonation session state. When present, the app is running as a tenant user.
   */
  impersonation?: ImpersonationState;
  /**
   * Active color scheme of the application.
   * undefined: not set, use value from user settings
   */
  colorScheme?: ColorSchemeName;
  onboardingComplete?: boolean;
  sidebarPinned: boolean;
}

const initialState: AppState = {
  colorScheme: undefined,
  sidebarPinned: false
};

// Slice
const { reducer, actions, selectors, reducerPath } = createSlice({
  name: 'root',
  reducerPath: 'app',
  initialState,
  reducers: {
    setToken: (state, { payload }: PayloadAction<AuthTokenDto | undefined>) => {
      if (payload) {
        state.token = {
          ...payload,
          stored_at: DateTime.utc().valueOf(),
          expire_time: DateTime.utc().valueOf() + payload.expires_in * 1000
        };
      } else {
        state.token = undefined;
      }
    },

    setTheme: (state, { payload }: PayloadAction<AppState['colorScheme']>) => {
      state.colorScheme = payload;
    },

    setOnboardingComplete: (state, { payload }: PayloadAction<boolean>) => {
      state.onboardingComplete = payload;
    },

    toggleSidebarPinned: (state, { payload }: PayloadAction<boolean | undefined>) => {
      state.sidebarPinned = isBoolean(payload) ? payload : !state.sidebarPinned;
    },

    startImpersonation: (state, { payload }: PayloadAction<AuthTokenDto>) => {
      state.impersonation = { impersonatingUser: state.token };
      state.token = {
        ...payload,
        stored_at: DateTime.utc().valueOf(),
        expire_time: DateTime.utc().valueOf() + payload.expires_in * 1000
      };
    },

    endImpersonation: state => {
      state.token = state.impersonation?.impersonatingUser;
      state.impersonation = undefined;
    },

    clear: state => ({ ...initialState, onboardingComplete: state.onboardingComplete })
  },
  selectors: {
    selectToken: state => state.token,
    selectAdminToken: state => state.impersonation?.impersonatingUser,
    selectActiveColorScheme: state => state.colorScheme,
    selectOnboardingComplete: state => state.onboardingComplete,
    selectSidebarPinned: state => state.sidebarPinned
  }
});

export const AppReducer = reducer;
export const AppActions = actions;
export const AppReducerPath = reducerPath;

export const {
  selectToken,
  selectAdminToken,
  selectActiveColorScheme,
  selectOnboardingComplete,
  selectSidebarPinned
} = selectors;
